import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../helpers/store";
import { getSubscriptionInfo, logout } from "./loginSlice";
import { useHistory, useLocation } from "react-router-dom";
import { ReducerStates, Views } from "../../helpers/constants";
import { updateLocalToken } from "../app/appSlice";
import { RedirectErrors } from "../../services/constants";
import { LeftPanel, MainContainer, RightPanel } from "./LoginLayout";
import { StackPanelLogo } from "./StackCarePanelLogo";
import AccountManagementHeader from "./AccountManagementHeader";
import LeftPanelFooter from "./LeftPanelFooter";
import { LoginDialog } from "../common/LoginDialog";
import SubscriptionManagementOptions from "../dashboard/SubscriptionManagementOptions";
import SignOut from "../dashboard/SignOut";

/**
 * Load subscription component
 */
export default function LoadSubscription() {
  /* ---- Hooks ---- */
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  /* ---- State ---- */
  const [open, setOpen] = React.useState(false);

  /* ---- Selectors ---- */
  const dashboardError = useSelector(
    (state: IAppState) => state.dashboardState.errorCode
  );
  const dashboardState = useSelector(
    (state: IAppState) => state.dashboardState.state
  );
  const loginState: ReducerStates = useSelector(
    (state: IAppState) => state.loginState.state
  );

  /* ---- Methods ---- */
  const retryLogin = () => {
    setOpen(false);
    const queryParams = new URLSearchParams(location.search);
    const email = queryParams.get("email");
    if (email) {
      dispatch(getSubscriptionInfo(email));
    }
  };

  /* ---- Utils ---- */
  const handleBackToLogin = () => {
    dispatch(logout());
    history.push(Views.LOGIN);
  };

  // Manage view flow
  useEffect(() => {
    if (ReducerStates[loginState] === ReducerStates.SUCCEEDED) {
      history.push(Views.SUBSCRIPTION);
    }
  }, [loginState, history]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const email = queryParams.get("email");
    const token = queryParams.get("token");
    if (email && token) {
      const expiration = queryParams.get("expiration");
      dispatch(
        updateLocalToken({
          access_token: token !== null ? token : "",
          access_expiration: expiration !== null ? expiration : "1200",
          token_type: "bearer",
        })
      );
      dispatch(getSubscriptionInfo(email));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    if (dashboardState === ReducerStates.FAILED) {
      let redirectErrorDetected = false;
      Object.keys(RedirectErrors).forEach((key) => {
        if (
          RedirectErrors[key as keyof typeof RedirectErrors] === dashboardError
        ) {
          redirectErrorDetected = true;
          history.push(Views.ACCESS_EXPIRED);
        }
      });
      if (!redirectErrorDetected) {
        setOpen(true);
      }
    }
  }, [history, dashboardState, dashboardError]);

  return (
    <React.Fragment>
      <MainContainer>
        <LeftPanel>
          <StackPanelLogo />
          <AccountManagementHeader />
          <SubscriptionManagementOptions />
          <SignOut />
          <LeftPanelFooter />
        </LeftPanel>
        <RightPanel children={undefined}></RightPanel>
      </MainContainer>
      <LoginDialog
        toggle={open}
        onClose={handleBackToLogin}
        onRetry={retryLogin}
      />
    </React.Fragment>
  );
}
